import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IOfficeAccountDTO } from '@common-frontend-submodule/interfaces/office-account/ioffice-account.interface';
import { IOfficeAccountCreationDTO } from '@common-frontend-submodule/interfaces/office-account/ioffice-account-creation.interface';
import { IOfficeAccountUpdateDTO } from '@common-frontend-submodule/interfaces/office-account/ioffice-account-update.interface';
import { IGetOfficeAccountsRequestDTO } from '@common-frontend-submodule/interfaces/office-account/iget-office-accounts-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { OfficeAccountRoutes } from '@common-frontend-submodule/enums/routes/office-account-routes.enum';

const apiClient = ApiClient.getInstance()

interface OfficeAccountContextProps {
  getById: (officeAccountId: string) => Promise<IApiResponseGenericDTO<IOfficeAccountDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<IOfficeAccountDTO[]>>;
  filter: (model: IGetOfficeAccountsRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IOfficeAccountDTO>>>;
  create: (model: IOfficeAccountCreationDTO) => Promise<IApiResponseGenericDTO<IOfficeAccountDTO>>;
  update: (officeAccountId: string, model: IOfficeAccountUpdateDTO) => Promise<IApiResponseGenericDTO<IOfficeAccountDTO>>;
  delete: (officeAccountId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const OfficeAccountContext = createContext<OfficeAccountContextProps | undefined>(undefined);

export const OfficeAccountProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (officeAccountId: string): Promise<IApiResponseGenericDTO<IOfficeAccountDTO>> => {
    return await apiClient.getRequest<IOfficeAccountDTO>(`${OfficeAccountRoutes.BasePrefix}/${officeAccountId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<IOfficeAccountDTO[]>> => {
    return await apiClient.getRequest<IOfficeAccountDTO[]>(`${OfficeAccountRoutes.BasePrefix}/${OfficeAccountRoutes.All}`);
  };

  const filter = async (model: IGetOfficeAccountsRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IOfficeAccountDTO>>> => {
    return await apiClient.postRequest<IGetOfficeAccountsRequestDTO, IPaginatedResultDTO<IOfficeAccountDTO>>(
      `${OfficeAccountRoutes.BasePrefix}/${OfficeAccountRoutes.List}`,
      model
    );
  };

  const create = async (model: IOfficeAccountCreationDTO): Promise<IApiResponseGenericDTO<IOfficeAccountDTO>> => {
    return await apiClient.postRequest<IOfficeAccountCreationDTO, IOfficeAccountDTO>(OfficeAccountRoutes.BasePrefix, model);
  };

  const update = async (officeAccountId: string, model: IOfficeAccountUpdateDTO): Promise<IApiResponseGenericDTO<IOfficeAccountDTO>> => {
    return await apiClient.putRequest<IOfficeAccountUpdateDTO, IOfficeAccountDTO>(`${OfficeAccountRoutes.BasePrefix}/${officeAccountId}`, model);
  };

  const deleteOfficeAccount = async (officeAccountId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${OfficeAccountRoutes.BasePrefix}/${officeAccountId}`);
  };

  return (
    <OfficeAccountContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteOfficeAccount }}>
      {children}
    </OfficeAccountContext.Provider>
  );
};

export const useOfficeAccountStore = () => {
  const context = useContext(OfficeAccountContext);
  if (!context) {
    throw new Error('useOfficeAccountStore must be used within a OfficeAccountProvider');
  }
  return context;
};
